import React, { Suspense } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera, Grid, Stats } from '@react-three/drei';
import styled from 'styled-components';
import { PiCubeBold, PiCursorClick } from 'react-icons/pi';

const ScreenContainer = styled.div`
  width: 100vw;
  height: 100vh;
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 18px;
  z-index: 10;
`;

const UIOverlay = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  color: white;
  z-index: 10;
  background: rgba(0, 0, 0, 0.5);
  padding: 15px;
  border-radius: 8px;
  backdrop-filter: blur(10px);
`;

// Sample 3D Scene Component
function Scene() {
  return (
    <>
      {/* Lighting */}
      <ambientLight intensity={0.4} />
      <directionalLight position={[10, 10, 5]} intensity={1} />

      {/* Sample 3D Objects */}
      <mesh position={[0, 1, 0]}>
        <boxGeometry args={[1, 1, 1]} />
        <meshStandardMaterial color="hotpink" />
      </mesh>

      <mesh position={[2, 1, 0]}>
        <sphereGeometry args={[0.5, 32, 32]} />
        <meshStandardMaterial color="orange" />
      </mesh>

      <mesh position={[-2, 1, 0]}>
        <cylinderGeometry args={[0.5, 0.5, 1, 32]} />
        <meshStandardMaterial color="lightblue" />
      </mesh>

      {/* Ground Grid */}
      <Grid
        renderOrder={-1}
        position={[0, -0.01, 0]}
        infiniteGrid
        cellSize={0.6}
        cellThickness={0.6}
        sectionSize={3.3}
        sectionThickness={1.5}
        sectionColor={[0.5, 0.5, 10]}
        fadeDistance={30}
      />
    </>
  );
}

const Project3DScreen: React.FC = () => {
  return (
    <ScreenContainer>
      {/* UI Overlay */}
      <UIOverlay>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <PiCubeBold style={{ fontSize: '18px' }} /> Project 3D Viewer
        </h3>
        <p style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: '4px 0' }}>
          <PiCursorClick style={{ fontSize: '16px' }} /> Use mouse to navigate:
        </p>
        <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
          <li>Left click + drag: Rotate</li>
          <li>Right click + drag: Pan</li>
          <li>Scroll: Zoom</li>
        </ul>
      </UIOverlay>

      {/* 3D Canvas */}
      <Canvas>
        <Suspense fallback={null}>
          {/* Camera */}
          <PerspectiveCamera makeDefault position={[5, 5, 5]} fov={60} />

          {/* Controls */}
          <OrbitControls
            enablePan={true}
            enableZoom={true}
            enableRotate={true}
            minDistance={1}
            maxDistance={50}
          />

          {/* Scene */}
          <Scene />

          {/* Performance Stats */}
          <Stats />
        </Suspense>
      </Canvas>

      {/* Loading Fallback */}
      <LoadingOverlay>
        Loading 3D Scene...
      </LoadingOverlay>
    </ScreenContainer>
  );
};

export default Project3DScreen;